/**
 * Formatting utilities for Analytics component
 */

/**
 * Get display name for breakdown options
 */
export function getBreakdownDisplayName(option: string): string {
  if (option === 'distributor') {
    return 'Partner';
  }
  return option.charAt(0).toUpperCase() + option.slice(1);
}

/**
 * Truncate labels longer than maxLength characters
 */
export function truncateLabel(label: string, maxLength: number = 20): string {
  if (!label || label.length <= maxLength) {
    return label;
  }
  return label.substring(0, maxLength) + '...';
}

/**
 * Truncate chart labels longer than 15 characters (for chart display only)
 * This is specifically for chart labels, not tooltips or tables
 */
export function truncateChartLabel(label: string): string {
  if (!label || label.length <= 15) {
    return label;
  }
  return label.substring(0, 15) + '...';
}

/**
 * Format product names (capitalize and replace FV Cream, Beans)
 */
export function formatProductName(name: string): string {
  if (!name) {
    return name;
  }
  // Replace FV Cream with French Vanilla (case-insensitive, handles with or without space)
  let formatted = name.replace(/FV\s*Cream/gi, 'French Vanilla');
  // Replace Beans with Coffee (case-insensitive)
  formatted = formatted.replace(/Beans/gi, 'Coffee');
  // Capitalize first letter of each word
  formatted = formatted
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  return formatted;
}

/**
 * Convert hex color to RGB string (e.g., "#3b82f6" -> "59, 130, 246")
 */
export function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) {
    return '59, 130, 246'; // Default blue
  }
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `${r}, ${g}, ${b}`;
}

/**
 * Lighten a hex color by a given amount (0-1)
 */
export function lightenColor(hex: string, amount: number): string {
  // Remove # if present
  hex = hex.replace('#', '');

  // Parse RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Lighten by blending with white
  const newR = Math.round(r + (255 - r) * amount);
  const newG = Math.round(g + (255 - g) * amount);
  const newB = Math.round(b + (255 - b) * amount);

  // Convert back to hex
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}

/**
 * Format date range label for display
 */
export function formatDateRangeLabel(
  dateRange: { startDate?: string; endDate?: string },
  compareDateRange?: { startDate?: string; endDate?: string }
): string {
  if (!dateRange.startDate || !dateRange.endDate) {
    return 'Current Period';
  }

  const formatDate = (dateStr: string): string => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const currentLabel = `${formatDate(dateRange.startDate)} - ${formatDate(dateRange.endDate)}`;

  if (compareDateRange && compareDateRange.startDate && compareDateRange.endDate) {
    const compareLabel = `${formatDate(compareDateRange.startDate)} - ${formatDate(compareDateRange.endDate)}`;
    return `${currentLabel} vs ${compareLabel}`;
  }

  return currentLabel;
}

